import ErrorBoundary from 'components/error-boundaries';
import ExchangesExpandableList from './exchanges';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from 'modules/shared-components/button/button';
import classNames from 'classnames';
import { useQueryParams } from 'modules/routing/useQueryParams';
import { useSchema } from 'modules/marketplace-performance-page/components/useSchema';
import { HeaderProps } from 'modules/marketplace-performance-page/validation/statsValidation';
import Tooltip from 'modules/shared-components/tooltip';
import { Socials } from 'api/types/httpsTypes/marketplace-performance';
import { toQueryString } from 'modules/routing/query-string';
import { CustomHtml } from 'modules/shared-components/CustomHtml';
import { useQuery } from '@tanstack/react-query';
import {
  marketplaceKeys,
  MarketplaceService,
} from 'api/services/httpServices/MarketplaceService';
import ShareBotModal from 'modules/marketplace-performance-page/components/ShareBotModal';
import { useToggle } from 'modules/utils/useToggle';
import Tag from 'modules/shared-components/data-display/tag';
import {
  LikeComponent,
  TraderLevel,
} from '../../../app/dashboard/marketplace/components/marketplace-cards';
import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useUserState } from 'modules/user/UserContext';
import { hasActiveJWT } from 'helpers/api/apiCore';
import Big from 'big.js';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import { NotificationDex } from '../../../app/dex/components/alerts/notification';

const Header = ({ info }: HeaderProps) => {
  const { pathname } = useLocation();
  const [queryParams] = useQueryParams(useSchema());
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [isOpenShareModal, { open: openShareModal, close: closeShareModal }] =
    useToggle();

  function isContactAvailable(socials: Socials) {
    let isAvailable = false;

    Object.keys(socials).forEach((channel: string) => {
      if (socials?.[channel as keyof Socials] !== null) {
        isAvailable = true;
      }
    });

    return isAvailable;
  }

  const { user } = useUserState();
  const isLoggedIn = user && 'access_token' in user && hasActiveJWT(user);
  const { data: likes } = useQuery({
    queryKey: marketplaceKeys.getLikes(),
    queryFn: () => MarketplaceService.getLikes(),
    enabled: isLoggedIn,
  });

  const isLiked = likes
    ? likes.strategies.some((id) => id === info.botInfo.id)
    : false;

  return (
    <ErrorBoundary>
      {info.botInfo?.type === 'self-hosted' ? (
        <div>
          <NotificationDex type="error">
            Following the enhancement of Mizar DCA Bots, please note that API
            Bots will be deprecated effective March 15th. For this reason, it's
            not possible to invest in this bot.
          </NotificationDex>
        </div>
      ) : (
        ''
      )}
      <div className="bg-white-100 dark:bg-black-800 xxs:p-6 rounded-md shadow-md">
        <div className="space-y-2">
          <div className="flex space-x-6">
            <div className="xxs:w-22 lg:w-48 flex justify-center items-center">
              <div className="">
                <div className="clip-mizar-hexagon w-22 lg:w-32">
                  <img
                    alt="trader logo"
                    src={info.imgUrl}
                    className="xxs:h-16 xxs:w-16 lg:h-32 lg:w-32"
                  />
                </div>
                <div className="lg:hidden">
                  <div className="flex  xxs:py-2 space-x-1">
                    {info.socials &&
                      Object.keys(info.socials).map(
                        (channel: string) =>
                          info?.socials?.[channel as keyof Socials] != null && (
                            <a
                              key={channel}
                              href={
                                info.socials?.[channel as keyof Socials] || ''
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i
                                className={classNames(
                                  'mdi text-xl hover:text-black-800 hover:dark:text-white-300 text-black-500 dark:text-white-50',
                                  `mdi-${
                                    channel === 'website' ? 'web' : channel
                                  }`
                                )}
                              />
                            </a>
                          )
                      )}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-3/5 items-center">
              <div className="space-y-2">
                <div className="">
                  <div className="flex items-center">
                    <div className="text-xl lg:text-4xl font-medium">
                      {info.name}{' '}
                    </div>
                  </div>

                  <div className="flex space-x-2 items-center">
                    <div className="text-sm lg:text-xl font-regular">
                      by {info.traderName}
                    </div>
                    <TraderLevel level={info.traderLevel?.name ?? ''} />
                  </div>
                </div>
                <div className="hidden lg:block text-xs lg:text-sm">
                  <div>
                    <CustomHtml
                      className={expandedDescription ? '' : 'line-clamp-5'}
                      html={info.description}
                    />
                    {!expandedDescription && (
                      <button
                        className="border-none text-blue-500 hover:text-blue-600 xxs:p-0"
                        onClick={() => setExpandedDescription(true)}
                      >
                        Read more
                      </button>
                    )}
                  </div>
                </div>
                <div className="lg:flex lg:space-x-1 hidden lg:block">
                  <Tag condensed color="neutral">
                    {info.botInfo.market?.toUpperCase()}
                  </Tag>
                  <Tag condensed color="neutral">
                    {info.botInfo.side?.toLowerCase() === 'long/short'
                      ? 'L/S'
                      : info.botInfo.side?.toLowerCase() === 'long'
                      ? 'LONG'
                      : 'SHORT'}
                  </Tag>
                  {info.botInfo.type !== 'self-hosted' && (
                    <div className="text-sm font-regular ">
                      <Link
                        className="hover:text-blue-600"
                        to={`/dashboard/marketplace/${
                          info.botInfo.id
                        }/performance/${info.botInfo.type}/${
                          info.botInfo.settingsId
                        }/settings?${toQueryString(queryParams)}`}
                      >
                        <i className="mdi mdi-settings-outline text-xs lg:text-sm" />{' '}
                        <span>Show Settings</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {info.botInfo?.type !== 'self-hosted' ? (
              <div className="hidden lg:block lg:flex-1 flex-row space-y-1">
                <div className="flex justify-between items-center">
                  <div className="">
                    <div className="">
                      Performance Fee:{' '}
                      {info?.performanceFee &&
                        (info?.performanceFee * 100)?.toFixed(1)}
                      %
                    </div>
                    {info?.performanceFeeChangeOn && (
                      <Tooltip
                        text={
                          Big(info?.newPerformanceFee ?? 0).lte(
                            info?.performanceFee ?? 0
                          )
                            ? `The performance fee has been decrease from ${Big(
                                info?.performanceFee ?? 0
                              )
                                .mul(100)
                                .toFixed(0)}% to ${Big(
                                info?.newPerformanceFee ?? 0
                              )
                                .mul(100)
                                .toFixed(0)}% on ${moment(
                                info?.performanceFeeChangeOn
                              ).format('DD/MM/YYYY HH:mm')}.`
                            : `The performance fee has been increased from ${Big(
                                info?.performanceFee ?? 0
                              )
                                .mul(100)
                                .toFixed(0)}% to ${Big(
                                info?.newPerformanceFee ?? 0
                              )
                                .mul(100)
                                .toFixed(
                                  0
                                )}%. Changes will be active on ${moment(
                                info?.performanceFeeChangeOn
                              ).format('DD/MM/YYYY HH:mm')}.`
                        }
                      >
                        <div
                          className={twMerge(
                            'flex justify-center xxs:px-1 xxs:py-1 rounded bg-red-500'
                          )}
                        >
                          <InformationCircleIcon className="h-3 w-3" />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div>
                  {info?.accessControlled && !info?.accessGranted ? (
                    <Button
                      as={Link}
                      size="large"
                      variant="primary"
                      className="text-white"
                      to={`/dashboard/${info.botInfo.type}/bots/${info.botInfo.settingsId}/request-access`}
                    >
                      REQUEST ACCESS
                    </Button>
                  ) : (
                    <Button
                      as={Link}
                      size="large"
                      className="text-white"
                      to={{
                        pathname: `/dashboard/marketplace/${info.botInfo.id}/activate`,
                        state: { sourceUrl: pathname },
                      }}
                      variant="magic"
                    >
                      INVEST NOW
                    </Button>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <LikeComponent
                    isLiked={isLiked}
                    botId={info.botInfo.id}
                    numberOfLikes={info.numberOfLikes ?? 0}
                  />
                  <Button
                    className="p-0 m-0 align-end"
                    onClick={openShareModal}
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 text-white-600 hover:text-blue-500" />
                  </Button>
                </div>

                {isOpenShareModal && (
                  <ShareBotModal
                    botId={info.botInfo.id}
                    handleClose={closeShareModal}
                  />
                )}
              </div>
            ) : (
              ''
            )}
          </div>
          <div className="w-full lg:hidden text-xs lg:text-sm">
            <div>
              <CustomHtml
                className={expandedDescription ? '' : 'line-clamp-5'}
                html={info.description}
              />
              {!expandedDescription && (
                <button
                  className="border-none text-blue-500 hover:text-blue-600 xxs:p-0"
                  onClick={() => setExpandedDescription(true)}
                >
                  Read more
                </button>
              )}
            </div>
          </div>
          <div className="lg:flex lg:space-x-6">
            <div className="hidden lg:block w-48">
              <div className=" flex-row justify-center items-center pt-2.5 px-2.5">
                <div className="text-sm font-regular">Contact the trader</div>
                {!isContactAvailable(info.socials) ? (
                  <div className="xxs:pt-1 text-xs">No contact available</div>
                ) : (
                  <div className="flex space-x-2.5">
                    {info.socials &&
                      Object.keys(info.socials).map(
                        (channel: string) =>
                          info?.socials?.[channel as keyof Socials] != null && (
                            <a
                              key={channel}
                              href={
                                info.socials?.[channel as keyof Socials] || ''
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i
                                className={classNames(
                                  'mdi text-xl hover:text-black-800 hover:dark:text-white-300 text-black-500 dark:text-white-50',
                                  `mdi-${
                                    channel === 'website' ? 'web' : channel
                                  }`
                                )}
                              />
                            </a>
                          )
                      )}
                  </div>
                )}
              </div>
            </div>
            <div className="hidden lg:block  md:w-2/5 2xl:w-3/5">
              <div className="text-sm font-regular">
                Supported Exchanges{' '}
                <Tooltip.Info text="The exchanges supported by this bot." />
              </div>
              <div className="overflow-y-auto max-h-24 md:max-h-32 lg:max-h-52">
                <ExchangesExpandableList
                  showNumber={4}
                  exchanges={info.exchanges || []}
                />
              </div>
            </div>
          </div>

          {info.botInfo?.type !== 'self-hosted' ? (
            <div className="lg:hidden my-2 space-y-1">
              <div className="flex justify-between items-center">
                <div className="">
                  Performance Fee:{' '}
                  {info?.performanceFee &&
                    (info.performanceFee * 100)?.toFixed(1)}
                  %
                </div>
                <Link
                  className="hover:text-blue-600"
                  to={`/dashboard/marketplace/${info.botInfo.id}/performance/${
                    info.botInfo.type
                  }/${info.botInfo.settingsId}/settings?${toQueryString(
                    queryParams
                  )}`}
                >
                  <i className="mdi mdi-settings-outline text-xs lg:text-sm" />{' '}
                  <span>Show Settings</span>
                </Link>
              </div>
              <div className="">
                {info?.accessControlled && !info?.accessGranted ? (
                  <Button
                    as={Link}
                    size="large"
                    variant="magic"
                    className="text-white"
                    to={`/dashboard/${info.botInfo.type}/bots/${info.botInfo.settingsId}/request-access`}
                  >
                    REQUEST ACCESS
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    size="large"
                    className="text-white"
                    to={{
                      pathname: `/dashboard/marketplace/${info.botInfo.id}/activate`,
                      state: { sourceUrl: pathname },
                    }}
                    variant="magic"
                  >
                    INVEST NOW
                  </Button>
                )}
              </div>

              <div className="flex justify-between items-center">
                <LikeComponent
                  isLiked={isLiked}
                  botId={info.botInfo.id}
                  numberOfLikes={info.numberOfLikes ?? 0}
                />
                <Button className="p-0 m-0" onClick={openShareModal}>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 text-white-600 hover:text-blue-500" />
                </Button>
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};
export default Header;
